import _ from 'lodash'
import { ANSWER_KEYS } from './answerFields.js'

export function getObjectArrayByPropertyFirst(
  objectArray,
  fieldItem,
  item,
  fieldReturn
) {
  // Given an array of Objects, return the first value for fieldReturn when Object[].fieldItem == item, otherwise empty
  let res = ''
  if (!_.isEmpty(objectArray)) {
    objectArray.forEach((entry) => {
      if (res === '' && entry[fieldItem] === item) {
        res = entry[fieldReturn]
      }
    })
  }
  return res
}

// NOTE Should be aligned with the worker.js
// console.log("Inside arraySortStringsByField(array, fieldName): ", _.cloneDeep(array), _.cloneDeep(fieldName))
// Sort the array using locale function comparison

export function arraySortStringsByField(array, fieldName) {
  // IMPORTANT: This file can't import lodash, so we need to rewrite the function in vanilla JS
  // if chainging logic here please update arrayUtils as well VENDICT app
  console.log(
    'Inside arraySortStringsByField(array, fieldName): ',
    array,
    fieldName
  )

  if (!Array.isArray(array) || typeof fieldName !== 'string') {
    console.error(
      'Invalid input: array must be an array and fieldName must be a string.'
    )
    return []
  }

  array.sort((a, b) => {
    const aValue =
      a && typeof a[fieldName] === 'string' ? a[fieldName].trim() : ''
    const bValue =
      b && typeof b[fieldName] === 'string' ? b[fieldName].trim() : ''

    return aValue.localeCompare(bValue)
  })

  return array
}

export function getArrayDetailsByField(array, field, item) {
  // Find the first array item details given a specific field value
  //console.log('Inside getArrayDetailsByField - array,field, item: ', array, field, item)
  return array.find((entry) => _.get(entry, field) == item)
}

export function getArrayIndexByField(array, field, item) {
  // NOTE Should be aligned with the worker.js
  if (_.isEmpty(array)) return
  // Find the first array index given a specific field value
  return array.findIndex((entry) => _.get(entry, field) == item)
}

export function mergeOneFieldFn(objValue, srcValue) {
  // Special merger function which chooses either source or object so that there is no merge but only field additions
  if (_.isArray(srcValue)) {
    return srcValue // do not merge arrays
  } else if (
    _.isEmpty(srcValue) &&
    !['number', 'boolean', 'string'].includes(typeof srcValue) // Number and boolean are seen as Empty, while empty strings should override
  ) {
    return objValue
  }
}

export function arraySortStrings(array) {
  // Sort the array using locale function comparison
  array.sort(function (a, b) {
    return a.localeCompare(b)
  })
}

export function arraySortNumberByField(array, fieldName, isDescending) {
  // Sort the array using number comparison (descending or ascending)
  array.sort(function (a, b) {
    return isDescending
      ? b[fieldName] - a[fieldName]
      : a[fieldName] - b[fieldName]
  })
}

export function uniqueArrayValues(array) {
  return _.isArray(array)
    ? array.filter((v, i, a) => _.findIndex(a, (e) => _.isEqual(e, v)) === i)
    : [] // retrieve only unique values - findIndex supports also objects
}

export function arrayObjArrayFieldOptionsGet(array, fieldName) {
  // Given an array of Object, retrieve all the unique options associated with the fieldName
  let allOptionsArr = []
  if (!_.isEmpty(array)) {
    array.forEach((entry) => {
      if (_.isArray(entry[fieldName])) {
        allOptionsArr = allOptionsArr.concat(entry[fieldName])
      } else if (_.isString(entry[fieldName]) || _.isNumber(entry[fieldName])) {
        allOptionsArr.push(entry[fieldName])
      }
    })
  }
  allOptionsArr = uniqueArrayValues(allOptionsArr) // unique values
  return allOptionsArr
}

/**
 * Filter and sort Trust Center data by order field first, then fall back to title/name
 * This ensures both Trust Center and Admin sides show the same order
 * @param {Array} array - The array of items to sort
 * @param {string} fallbackField - Field name to use for sorting when order is not available (default: 'title')
 * @returns {Array} - Sorted array
 */
export function filterTrustCenterData(array, fallbackField = 'title') {
  if (!Array.isArray(array)) {
    console.error(
      'filterTrustCenterData: Invalid input - array must be an array'
    )
    return []
  }

  // Create a copy to avoid mutating the original array
  const sortedArray = [...array]

  sortedArray.sort((a, b) => {
    // First, sort by order field if it exists
    const aOrder = a?.order
    const bOrder = b?.order

    // If both have order values, sort by order
    if (
      aOrder !== undefined &&
      aOrder !== null &&
      bOrder !== undefined &&
      bOrder !== null
    ) {
      return aOrder - bOrder
    }

    // If only a has order, it comes first
    if (aOrder !== undefined && aOrder !== null) {
      return -1
    }

    // If only b has order, it comes first
    if (bOrder !== undefined && bOrder !== null) {
      return 1
    }

    // If neither has order, fall back to fallbackField (title or name)
    const aValue =
      a && typeof a[fallbackField] === 'string' ? a[fallbackField].trim() : ''
    const bValue =
      b && typeof b[fallbackField] === 'string' ? b[fallbackField].trim() : ''

    return aValue.localeCompare(bValue)
  })

  return sortedArray
}

/**
 * Search recursively in an object/array for a query string
 * @param {any} value - The value to search in
 * @param {string} lowerQuery - The lowercase query string
 * @param {number} maxDepth - Maximum depth to search (prevents infinite loops)
 * @returns {boolean} - True if query found
 */
function searchRecursively(value, lowerQuery, maxDepth = 10) {
  if (maxDepth <= 0) {
    return false
  }

  // If value is a string, check if it contains the query
  if (typeof value === 'string') {
    return value.toLowerCase().includes(lowerQuery)
  }

  // If value is a number, convert to string and check
  if (typeof value === 'number') {
    return value.toString().toLowerCase().includes(lowerQuery)
  }

  // If value is an array, search in each element
  if (Array.isArray(value)) {
    return value.some((item) =>
      searchRecursively(item, lowerQuery, maxDepth - 1)
    )
  }

  // If value is an object, search in each property
  if (_.isPlainObject(value)) {
    return Object.values(value).some((val) =>
      searchRecursively(val, lowerQuery, maxDepth - 1)
    )
  }

  return false
}

/**
 * Filter array by deep search across multiple fields or recursively in all fields
 * @param {Array} items - Array of objects to filter
 * @param {string} query - Search query string
 * @param {Array<string>} [fieldsToSearch] - Optional array of field paths to search in (e.g., ['displayId', 'question.question', 'answer.answers'])
 *                                            If not provided, searches recursively in all fields
 * @returns {Array} - Filtered array of items matching the query
 */
export function filterByDeepSearch(items, query, fieldsToSearch = null) {
  if (!query || !_.isString(query)) {
    return items
  }

  const lowerQuery = query.toLowerCase().trim()

  if (!lowerQuery) {
    return items
  }

  return items.filter((item) => {
    // If specific fields are provided, search only in those fields
    if (
      fieldsToSearch &&
      Array.isArray(fieldsToSearch) &&
      fieldsToSearch.length > 0
    ) {
      return fieldsToSearch.some((fieldPath) => {
        const value = _.get(item, fieldPath)

        // If the value is an object (like answers object with A1, A2, etc.), search in all its values
        if (_.isPlainObject(value)) {
          return Object.values(value).some((val) => {
            if (typeof val === 'string') {
              return val.toLowerCase().includes(lowerQuery)
            }
            return false
          })
        }

        // If the value is a string, check directly
        if (typeof value === 'string') {
          return value.toLowerCase().includes(lowerQuery)
        }

        // If the value is a number, convert to string
        if (typeof value === 'number') {
          return value.toString().toLowerCase().includes(lowerQuery)
        }

        return false
      })
    }

    // Otherwise, search recursively in all fields
    return searchRecursively(item, lowerQuery)
  })
}

/**
 * Filter questionnaire cards by deep search across multiple fields
 * @param {Array} cards - Array of card objects to filter
 * @param {string} query - Search query string
 * @returns {Array} - Filtered array of cards matching the query
 */
export function filterCardsByDeepSearch(cards, query) {
  if (!query || !_.isString(query)) {
    return cards
  }

  const lowerQuery = query.toLowerCase().trim()

  if (!lowerQuery) {
    return cards
  }

  return cards.filter((card) => {
    // Search in displayId (question number)
    if (card.displayId && String(card.displayId).toLowerCase().includes(lowerQuery)) {
      return true
    }

    // Search in question text (flattened to root level after transform)
    if (card.question && String(card.question).toLowerCase().includes(lowerQuery)) {
      return true
    }

    // Search in answer.answers object (recursively searches all A1, A2, etc. values)
    if (card.answer?.answers) {
      const answerValues = Object.values(card.answer.answers)
      if (answerValues.some((val) => {
        if (typeof val === 'string') {
          return val.toLowerCase().includes(lowerQuery)
        }
        if (typeof val === 'number') {
          return String(val).toLowerCase().includes(lowerQuery)
        }
        if (Array.isArray(val)) {
          return val.some((item) => String(item).toLowerCase().includes(lowerQuery))
        }
        return false
      })) {
        return true
      }
    }

    // Search in root-level answer fields (A1, A2, etc. - flattened after transform)
    // Check all properties that start with 'A' followed by digits
    for (const key in card) {
      if (ANSWER_KEYS.includes(key)) {
        const value = card[key]
        if (value !== undefined && value !== null) {
          if (typeof value === 'string' && value.toLowerCase().includes(lowerQuery)) {
            return true
          }
          if (typeof value === 'number' && String(value).toLowerCase().includes(lowerQuery)) {
            return true
          }
          if (Array.isArray(value) && value.some((item) => String(item).toLowerCase().includes(lowerQuery))) {
            return true
          }
        }
      }
    }

    // Search in answer.answersHtml if it exists
    if (card.answer?.answersHtml) {
      const answerHtmlValues = Object.values(card.answer.answersHtml)
      if (answerHtmlValues.some((val) => {
        if (typeof val === 'string') {
          return val.toLowerCase().includes(lowerQuery)
        }
        if (Array.isArray(val)) {
          return val.some((item) => String(item).toLowerCase().includes(lowerQuery))
        }
        return false
      })) {
        return true
      }
    }

    // Search in genAns.answer (AI-generated answers)
    if (card.genAns?.answer && String(card.genAns.answer).toLowerCase().includes(lowerQuery)) {
      return true
    }

    return false
  })
}
